import cx from 'classnames';
import {useCallback, useEffect, useMemo, useReducer, useState} from 'react';
import {createPortal} from 'react-dom';
import {useSelector} from 'react-redux';
import {Store} from 'redux';
import {NamedStepsNav} from '~/CreateFabricInvitation/components/NavHeader/components/NamedStepsNav';
import {NamedStepsNavCTAs} from '~/CreateFabricInvitation/components/NavHeader/components/NamedStepsNavCTAs';
import {
  DirectionParam,
  EventStep,
  STEP_NAMES,
  StepKey,
  StepOrDirection,
} from '~/CreateFabricInvitation/components/NavHeader/types';
import {getRawSteps} from '~/CreateFabricInvitation/components/NavHeader/utils';
import {
  getNavigationSteps,
  navigateToRoute,
  navigateToRoute as navigateToRouteCreateFabric,
} from '~/CreateFabricInvitation/navigation';
import {createSteps} from '~/CreateFabricInvitation/navigation/createSteps';
import {backToGallery} from '~/CreateFabricInvitation/navigation/utils';
import {validateEventDetails} from '~/CreateFabricInvitation/navigation/validation';
import {eventInEditMode} from '~/CreateFabricInvitation/reducers/details/selectors';
import {
  selectPristineBackground,
  selectPristineEnvelopeDesign,
} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {isProOrPartyPassUser} from '~/CreateFabricInvitation/utils/experiments';
import {isInAppMode} from '~/common/checkout/utils/app';
import {useNavBottomState} from '~/common/FabricInvitation/EventCreateDesign/components/NavBottom/hooks/useNavBottomState';
import {useValidation} from '~/common/FabricInvitation/EventCreateDesign/hooks/useValidation';
import {INIT_UNLOCK_STATE, UNLOCK_STEPS} from '~/common/fabric/constants';
import {
  createInitialState,
  unlockedStepsReducer,
} from '~/common/fabric/reducers/createFlowNav/reducer';
import {selectIsDetailsPristine} from '~/common/fabric/reducers/details/selectors';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {selectPristine, selectSavingStep} from '~/common/fabric/reducers/fabricDesign/selectors';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {LoadingOverlay} from '~library/molecules/LoadingOverlay';
import {ChevronIcon} from '~library/svg/ChevronIcon';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {DiamondFillIcon} from '~library/svg/DiamondFillIcon';
import {ProLogoIcon} from '~library/svg/v2/ProLogoIcon';
import {scrollElementIntoViewById} from '~library/utils/misc';
import {premiumColor} from '~libSass/base/_exports.sass';
import * as styles from './NamedStepsNavWrapper.module.scss';

export interface NamedStepsNavWrapperProps {
  currentRoute: EventStep;
  isPremium: boolean;
  eventId: string | null;
  totalSteps: number;
  handleShowLeaveModal: () => Promise<void>;
  onSave: () => void;
  premiumPreview?: boolean;
  store: Store;
  navigateToRouteHook?: typeof navigateToRouteCreateFabric;
}

/**
 * TODO: Break out this component into smaller sub-components to improve readability and maintainability.
 */

// biome-ignore lint/complexity/noExcessiveLinesPerFunction: workin on it
export function NamedStepsNavWrapper({
  currentRoute,
  isPremium,
  eventId,
  totalSteps,
  handleShowLeaveModal,
  onSave,
  premiumPreview = false,
  store,
  navigateToRouteHook = navigateToRouteCreateFabric,
}: NamedStepsNavWrapperProps) {
  const {showUpsellModal, handleModals} = useNavBottomState();
  const inAppMode = isInAppMode();
  const isLoading: boolean = useSelector(selectSavingStep);
  const isLargishAndUp = useIsLargishAndUp();
  const showProLogo = isProOrPartyPassUser(window.subscriptionPlanName);
  const stepConfig = useMemo(() => {
    return Object.entries(createSteps)
      .filter(([key]) => !['meta', 'exit'].includes(key))
      .reduce(
        (acc, [key, value]) => {
          acc[key as StepKey] = value as EventStep;
          return acc;
        },
        {} as Record<StepKey, EventStep>
      );
  }, []);
  const inEditMode = useSelector(eventInEditMode) as boolean;
  const rawSteps = useMemo(
    () => getRawSteps(getNavigationSteps(inEditMode, store.getState())),
    [inEditMode, store]
  );
  const stepsToShow = useMemo(() => {
    const potentialStepsToShow: StepKey[] = [
      'customize',
      'details',
      'gifting',
      'preview',
      'guests',
    ];
    const stepsMap: Map<number, EventStep> = new Map();
    Object.values(rawSteps).forEach((step) => {
      stepsMap.set(step.id, step); // Replace any existing step with the same `id`
    });
    return Array.from(stepsMap.values())
      .map((step) => step.name as StepKey)
      .filter((step) => potentialStepsToShow.includes(step));
  }, [rawSteps]);

  const activeStep = currentRoute.name as StepKey;
  const hasBeenSaved = Boolean(eventId);
  const isFirstStep = currentRoute.name === 'customize';
  const isCheckoutStep = currentRoute.name === 'review';
  const stepSpecificBackButtonText =
    isFirstStep && !hasBeenSaved ? 'Back to browse' : 'Back to edit';

  // Unlocked steps state
  const initialState = useMemo(
    () =>
      createInitialState({
        inEditMode,
        activeStep: activeStep ?? '',
        rawSteps,
      }),
    [inEditMode, activeStep, rawSteps]
  );
  const [unlockedSteps, dispatchReducer] = useReducer(unlockedStepsReducer, initialState);

  // Pristine selectors
  const isDetailsPristine = useSelector(selectIsDetailsPristine) as boolean;
  const isDesignPristine = useSelector(selectPristine) as boolean;
  const isUyoPristine = useSelector(selectPristineEnvelopeDesign) as boolean;
  const isBackgroundPristine = useSelector(selectPristineBackground) as boolean;
  const isPristine = isDesignPristine && isBackgroundPristine && isUyoPristine && isDetailsPristine;

  const [nextRoute, setNextRoute] = useState<StepKey | undefined>(undefined);

  const getStepConfig = useCallback(
    (stepOrDirection: StepKey | DirectionParam) => {
      const isStepKey = STEP_NAMES.includes(stepOrDirection as StepKey);
      if (isStepKey) {
        const nextRoute = currentRoute.nextRoute;
        if (typeof nextRoute === 'string' && STEP_NAMES.includes(nextRoute as StepKey)) {
          return stepConfig[nextRoute as StepKey];
        }

        return stepConfig[currentRoute.name as StepKey];
      }
      if (stepOrDirection === 'next') {
        const nextRoute = currentRoute.nextRoute;
        if (typeof nextRoute === 'string' && STEP_NAMES.includes(nextRoute as StepKey)) {
          return stepConfig[nextRoute as StepKey];
        }
        return stepConfig[currentRoute.name as StepKey];
      }
      if (stepOrDirection === 'previous') {
        const prevRoute = currentRoute.previousRoute as StepKey | undefined;
        if (prevRoute && stepConfig[prevRoute]) {
          return stepConfig[prevRoute];
        }
        return stepConfig[currentRoute.name as StepKey];
      }
      return stepConfig[currentRoute.name as StepKey];
    },
    [currentRoute.nextRoute, currentRoute.previousRoute, currentRoute.name, stepConfig]
  );

  const getStepAttr = useCallback(
    (stepName: StepKey | DirectionParam, attr: keyof EventStep) => {
      const config = getStepConfig(stepName);
      return config[attr];
    },
    [getStepConfig]
  );

  const setIsLoading = useCallback(
    (loading: boolean) => {
      store.dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {savingStep: loading},
      });
    },
    [store]
  );

  const handleUnlockStep = useCallback(
    async (step: string) => {
      const currentStep = rawSteps[step];
      const updatedSteps = Object.values(rawSteps)
        .filter((s) => currentStep?.id !== undefined && s.id <= currentStep.id)
        .map((s) => s.name);

      dispatchReducer({
        type: UNLOCK_STEPS,
        payload: updatedSteps,
      });
    },
    [rawSteps]
  );

  const {validate: postValidate, ErrorModal} = useValidation();
  const handleReviewNextButtonClick = useCallback(async () => {
    return await validateEventDetails(
      store,
      currentRoute.name === 'preview' ? postValidate : undefined,
      true
    );
  }, [currentRoute.name, store, postValidate]);

  const resolvedCurrentRoute = {
    ...currentRoute,
    nextRoute:
      typeof currentRoute.nextRoute === 'function'
        ? currentRoute.nextRoute(store)
        : currentRoute.nextRoute,
  };

  const handleNextClick = useCallback(async () => {
    try {
      setIsLoading(true);
      await navigateToRouteHook(currentRoute.nextRoute, store, false, false, [true]);

      await handleUnlockStep(currentRoute.nextRoute as StepOrDirection);
      return true;
    } catch {
      setIsLoading(false);
      return false;
    }
  }, [currentRoute.nextRoute, store, setIsLoading, navigateToRouteHook, handleUnlockStep]);

  const handleBackClick = useCallback(async () => {
    if (isFirstStep && isPristine) {
      return backToGallery(true);
    }
    if (isCheckoutStep) {
      return navigateToRoute('preview', store);
    }
    if (!isPristine) {
      setIsLoading(false);
      return await handleShowLeaveModal();
    }
    return navigateToRoute('previous', store);
  }, [isFirstStep, isPristine, handleShowLeaveModal, isCheckoutStep, store, setIsLoading]);

  // Loading overlay
  const renderLoadingPortal = useCallback(() => {
    const fabricRoot = document.getElementById('fabric-create-invitation-root');
    if (!fabricRoot || !isLoading) return null;
    return createPortal(
      <LoadingOverlay loadingMessage="Loading..." show={isLoading} />,
      fabricRoot
    );
  }, [isLoading]);

  const preClickHandler = useCallback(
    async (stepOrDirection: StepKey | DirectionParam) => {
      const isStepKey = STEP_NAMES.includes(stepOrDirection as StepKey);

      if (['preview', 'guests'].includes(stepOrDirection) && !isLargishAndUp)
        scrollElementIntoViewById('guests-step');

      let goingForward = stepOrDirection === 'next';

      if (isStepKey) {
        const stepId = getStepAttr(stepOrDirection as StepKey, 'id');
        const activeStepId = getStepAttr(activeStep, 'id');
        goingForward = (stepId ?? 0) > (activeStepId ?? 0);
      }

      let func: ((step: StepOrDirection) => Promise<unknown>) | undefined;
      if (stepOrDirection === 'previous') {
        func = handleBackClick;
      } else if (stepOrDirection === 'next') {
        func = handleNextClick;
      } else {
        func = (s: StepOrDirection) => navigateToRoute(s, store);
      }
      setIsLoading(true);
      const isValidationStep = ['preview', 'guests'].includes(currentRoute.name);
      if (isValidationStep && goingForward) {
        const isValid = await handleReviewNextButtonClick?.();
        if (!isValid) {
          setIsLoading(false);
          return;
        }
      }

      if (premiumPreview) {
        setNextRoute(stepOrDirection as StepKey);
        handleModals.toggleUpsellModal();
        return;
      }

      const result = await func(isStepKey ? (stepOrDirection as StepOrDirection) : activeStep);

      if (!result) {
        setIsLoading(false);
        return;
      }

      await handleUnlockStep(stepOrDirection as StepOrDirection);
      setIsLoading(false);
    },
    [
      getStepAttr,
      handleBackClick,
      handleNextClick,
      store,
      handleUnlockStep,
      setIsLoading,
      handleReviewNextButtonClick,
      premiumPreview,
      handleModals.toggleUpsellModal,
      currentRoute.name,
      activeStep,
      isLargishAndUp,
    ]
  );

  const modalHandlers = useMemo(
    () => ({
      ...handleModals,
      downgradeAndContinue: () => handleModals.downgradeAndContinue(nextRoute),
      upgradeAndContinue: () => handleModals.upgradeAndContinue(nextRoute),
    }),
    [handleModals, nextRoute]
  );

  // Navigation class
  const navClassName = useMemo(
    () =>
      cx(styles.namedStepsNav, {
        [styles.frp]: currentRoute.id > totalSteps,
        [styles.namedStepsNavCheckout]: isCheckoutStep,
      }),
    [currentRoute.id, totalSteps, isCheckoutStep]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: run once
  useEffect(() => {
    dispatchReducer({
      type: INIT_UNLOCK_STATE,
      payload: {
        inEditMode,
        activeStep,
        rawSteps,
      },
    });
  }, []);

  useEffect(() => {
    if (['preview', 'guests'].includes(activeStep) && !isLargishAndUp)
      scrollElementIntoViewById('guests-step');
  }, [activeStep, isLargishAndUp]);

  if (currentRoute.showMegaNav) {
    return null;
  }

  return (
    <nav className={navClassName}>
      {!inAppMode && (
        <div className={styles.namedStepsNavLeft}>
          {isLargishAndUp || (!isLargishAndUp && isCheckoutStep) ? (
            <Button
              variant="secondary"
              size={isLargishAndUp ? 'medium' : 'small'}
              data-qa-id="back-to-browse"
              onClick={() => preClickHandler('previous')}
            >
              {(isFirstStep && !hasBeenSaved) || isCheckoutStep ? (
                <>
                  {isFirstStep && !hasBeenSaved ? (
                    <ChevronIcon direction="left" ratio={0.75} />
                  ) : null}
                  {stepSpecificBackButtonText}
                </>
              ) : (
                'Save & exit'
              )}
            </Button>
          ) : (
            <Button
              data-qa-id="close"
              variant="tertiary"
              size="medium"
              disableProcessing
              onClick={() => preClickHandler('previous')}
            >
              <CloseLineIcon ratio={0.75} />
            </Button>
          )}
          {isLargishAndUp && (isPremium || showProLogo) ? (
            <Typography
              variant="paragraph2"
              color="premiumColor"
              className={styles.namedStepsNavPremiumBadge}
              aria-hidden
              as="span"
            >
              {showProLogo ? (
                <ProLogoIcon />
              ) : (
                <>
                  <DiamondFillIcon color={premiumColor} ratio={0.25} /> Premium
                </>
              )}
            </Typography>
          ) : null}
        </div>
      )}
      <NamedStepsNavCTAs
        isPremium={isPremium}
        currentRoute={resolvedCurrentRoute}
        preClickHandler={preClickHandler}
        premiumPreview={premiumPreview}
        onSave={onSave}
        eventId={eventId}
        showUpsellModal={showUpsellModal}
        handleModals={modalHandlers}
      />
      <NamedStepsNav
        isPremium={isPremium}
        stepsToShow={stepsToShow}
        rawSteps={rawSteps}
        unlockedSteps={unlockedSteps}
        preClickHandler={preClickHandler}
        currentRoute={currentRoute}
        setIsLoading={setIsLoading}
      />
      {renderLoadingPortal()}
      {ErrorModal}
    </nav>
  );
}
